/**
 * ThemeProvider — applies a theme to the document and exposes it to React.
 *
 * - Emits every token as a CSS custom property on `<html>` (`:root`).
 * - Sets `data-theme="<name>"`, `data-mode="light|dark"` and `color-scheme`.
 * - Injects the theme's web-font stylesheet, favicon, touch icon and
 *   `<meta name="theme-color">` (all URLs — no assets live in the repo).
 * - Mode: light-first. The persisted preference (`ohf-portal-mode`) is
 *   `light | dark | system`; `system` follows `prefers-color-scheme`.
 *
 * `applyThemeToDocument` is a plain DOM function so `main.tsx` can paint the
 * default theme before React mounts (no unstyled first frame).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { getTheme, resolveThemeName } from "./registry.js";
import { themeToCssVariables } from "./tokens.js";
import type { ModePreference, Theme, ThemeMode } from "./types.js";

export const MODE_STORAGE_KEY = "ohf-portal-mode";

export function readModePreference(storage: Pick<Storage, "getItem"> | null = safeStorage()): ModePreference {
  try {
    const v = storage?.getItem(MODE_STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    /* private mode / blocked storage */
  }
  return "system";
}

export function writeModePreference(pref: ModePreference, storage: Pick<Storage, "setItem"> | null = safeStorage()): void {
  try {
    storage?.setItem(MODE_STORAGE_KEY, pref);
  } catch {
    /* ignore quota / private mode */
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function systemPrefersDark(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/** Light-first: `system` resolves to dark only when the OS says so. */
export function resolveMode(pref: ModePreference, prefersDark: boolean = systemPrefersDark()): ThemeMode {
  if (pref === "light" || pref === "dark") return pref;
  return prefersDark ? "dark" : "light";
}

let appliedVariableNames: string[] = [];

function upsertHeadLink(doc: Document, id: string, attrs: Record<string, string>): HTMLLinkElement {
  let el = doc.getElementById(id) as HTMLLinkElement | null;
  if (!el) {
    el = doc.createElement("link");
    el.id = id;
    doc.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  return el;
}

function removeHeadEl(doc: Document, id: string): void {
  doc.getElementById(id)?.remove();
}

/** Paint a theme + mode onto the document. Idempotent; safe to call often. */
export function applyThemeToDocument(theme: Theme, mode: ThemeMode, doc: Document = document): void {
  const root = doc.documentElement;
  const vars = themeToCssVariables(theme, mode);
  for (const name of appliedVariableNames) {
    if (!(name in vars)) root.style.removeProperty(name);
  }
  for (const [name, value] of Object.entries(vars)) root.style.setProperty(name, value);
  appliedVariableNames = Object.keys(vars);

  root.setAttribute("data-theme", theme.name);
  root.setAttribute("data-mode", mode);
  root.style.colorScheme = theme.modes[mode].colorScheme;

  if (theme.fonts.stylesheetHref) {
    upsertHeadLink(doc, "theme-fonts-preconnect", {
      rel: "preconnect",
      href: "https://fonts.gstatic.com",
      crossorigin: "",
    });
    upsertHeadLink(doc, "theme-fonts", { rel: "stylesheet", href: theme.fonts.stylesheetHref });
  } else {
    removeHeadEl(doc, "theme-fonts-preconnect");
    removeHeadEl(doc, "theme-fonts");
  }
  upsertHeadLink(doc, "theme-favicon", { rel: "icon", href: theme.logo.favicon });
  if (theme.logo.touchIcon) {
    upsertHeadLink(doc, "theme-touch-icon", { rel: "apple-touch-icon", href: theme.logo.touchIcon });
  } else {
    removeHeadEl(doc, "theme-touch-icon");
  }
  let meta = doc.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = doc.createElement("meta");
    meta.name = "theme-color";
    doc.head.appendChild(meta);
  }
  meta.content = mode === "dark" ? theme.modes.dark.bg : theme.logo.themeColor;
}

export interface ThemeContextValue {
  theme: Theme;
  themeName: string;
  mode: ThemeMode;
  modePreference: ModePreference;
  setModePreference: (pref: ModePreference) => void;
  toggleMode: () => void;
  /** Switch the active theme by name (unknown names fall back to the default). */
  setThemeName: (name: string | null | undefined) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export interface ThemeProviderProps {
  /** Candidates in priority order, e.g. `[config.theme, VITE_THEME]`. */
  themeName?: string | null;
  /** Force a mode (tests); otherwise the persisted preference + system default. */
  initialModePreference?: ModePreference;
  children: ReactNode;
}

export function ThemeProvider({ themeName, initialModePreference, children }: ThemeProviderProps) {
  const [name, setName] = useState(() => resolveThemeName(themeName));
  const [modePreference, setPref] = useState<ModePreference>(
    () => initialModePreference ?? readModePreference(),
  );
  const [prefersDark, setPrefersDark] = useState(systemPrefersDark);

  // Follow a changed `themeName` prop (config arrives after first paint).
  useEffect(() => {
    setName(resolveThemeName(themeName));
  }, [themeName]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setPrefersDark(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const theme = getTheme(name);
  const mode = resolveMode(modePreference, prefersDark);

  useLayoutEffect(() => {
    applyThemeToDocument(theme, mode);
  }, [theme, mode]);

  const setModePreference = useCallback((pref: ModePreference) => {
    setPref(pref);
    writeModePreference(pref);
  }, []);

  const toggleMode = useCallback(() => {
    setModePreference(mode === "dark" ? "light" : "dark");
  }, [mode, setModePreference]);

  const setThemeName = useCallback((next: string | null | undefined) => {
    setName(resolveThemeName(next));
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, themeName: theme.name, mode, modePreference, setModePreference, toggleMode, setThemeName }),
    [theme, mode, modePreference, setModePreference, toggleMode, setThemeName],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
